/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useReducer, useRef } from 'react';
import type { IResponseRequest } from '../../Interfaces/Dto';

enum RequestActions {
  REQUEST = 'REQUEST_STATUS_REQUEST',
  SUCCESS = 'REQUEST_STATUS_SUCCESS',
  FAILURE = 'REQUEST_STATUS_FAILURE',
}

interface IAction<T = any> {
  type: RequestActions;
  payload?: T;
}

interface IRequestState<T = any> {
  isLoading: boolean;
  error?: string;
  data?: IResponseRequest<T>;
  // promise?: Promise<any>;
}

function useRequest<DataType = any>(
  initialStateProps?: Partial<IRequestState>,
) {
  const requestReducer = useCallback(
    (
      state: IRequestState<DataType>,
      action: IAction,
    ): IRequestState<DataType> => {
      switch (action.type) {
        case RequestActions.REQUEST:
          return {
            ...state,
            isLoading: true,
          };
        case RequestActions.SUCCESS:
          return {
            ...state,
            isLoading: false,
            data: action.payload,
            error: '',
          };
        case RequestActions.FAILURE:
          return {
            ...state,
            isLoading: false,
            error: action.payload,
          };
        default:
          return state;
      }
    },
    [],
  );

  const initialState: IRequestState<DataType> = {
    isLoading: false,
    error: '',
    ...initialStateProps,
  };

  const isCancelled = useRef(false);

  const [{ data, error, isLoading }, dispatch] = useReducer(
    requestReducer,
    initialState,
  );

  useEffect(() => {
    return () => {
      isCancelled.current = true;
    };
  }, []);

  const request = useCallback(
    async (promise: Promise<IResponseRequest<DataType>>) => {
      dispatch({ type: RequestActions.REQUEST });
      try {
        const result = await promise;

        if (!isCancelled.current) {
          dispatch({ type: RequestActions.SUCCESS, payload: result });
        }

        return result;
      } catch (err: any) {
        if (!isCancelled.current) {
          const errorMessage = err.response?.data.errors;

          if (errorMessage) {
            dispatch({
              type: RequestActions.FAILURE,
              payload: errorMessage.join(' - '),
            });
            return null;
          }

          const error = Error(err);

          dispatch({ type: RequestActions.FAILURE, payload: error.message });
        }

        return null;
      }
    },
    [],
  );

  return { data, error, isLoading, request };
}

export default useRequest;
